import { client } from './index';
import {
  ChatbotConversation,
  ChatbotMessage,
  ChatbotModuleContext,
  ChatbotReference,
  ExportArtifact,
  MessageSource,
  PollMessageResponse,
  ReportIssueRequest,
  SendMessageResponse,
} from '../types/askDataScope';
import { ChartData, ChartSpec } from '../types/dashboards';

export const askDataScopeApi = {
  getConversations: () =>
    client.get<ChatbotConversation[]>('/chatbot/conversations'),

  createConversation: (moduleContext?: ChatbotModuleContext) =>
    client.post<{ id: number; module_context: ChatbotModuleContext }>('/chatbot/conversations', {
      module_context: moduleContext,
    }),

  getMessages: (conversationId: number) =>
    client.get<ChatbotMessage[]>(`/chatbot/conversations/${conversationId}/messages`),

  sendMessage: (conversationId: number, content: string, options?: { start_date?: string | null; end_date?: string | null; module_context?: ChatbotModuleContext; references?: ChatbotReference[] }) =>
    client.post<SendMessageResponse>(`/chatbot/conversations/${conversationId}/messages`, {
      content,
      start_date: options?.start_date || undefined,
      end_date: options?.end_date || undefined,
      module_context: options?.module_context ?? undefined,
      // "@" references: entities the user pointed at, by id (never name). The
      // backend re-validates each against the user's permissions (U7).
      references: options?.references?.length ? options.references : undefined,
    }),

  pollMessage: (conversationId: number, messageId: number) =>
    client.get<PollMessageResponse>(
      `/chatbot/conversations/${conversationId}/messages/${messageId}/poll`,
    ),

  reportIssue: (conversationId: number, data: ReportIssueRequest) =>
    client.post<{ success: boolean }>(`/chatbot/conversations/${conversationId}/report`, data),

  attachExcel: (conversationId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return client.post<SendMessageResponse>(
      `/chatbot/conversations/${conversationId}/attach_excel`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  confirmPendingAction: (id: number) =>
    client.post<{ pending_action_id: number; status: string; assistant_message: ChatbotMessage }>(
      `/chatbot/pending_actions/${id}/confirm`,
    ),

  rejectPendingAction: (id: number) =>
    client.post<{ pending_action_id: number; status: string }>(
      `/chatbot/pending_actions/${id}/reject`,
    ),

  createSupportHandoff: (conversationId: number, content: string) =>
    client.post<{ intercom_conversation_id: string }>(
      `/chatbot/conversations/${conversationId}/support_handoff`,
      { content },
    ),

  // Records a support-chat handoff in the DB so it is queryable server-side
  // (replaces the client-side Amplitude tracking). Best-effort: callers fire it
  // and ignore failures so telemetry never blocks the handoff.
  recordHandoffOpen: (
    conversationId: number,
    data: {
      source: 'auto' | 'button';
      delivery: 'prefilled' | 'sent' | 'prefilled_fallback';
      intercom_conversation_id?: string;
    },
  ) => client.post<void>(`/chatbot/conversations/${conversationId}/handoff_event`, data),

  // AskDataScope V2 "Personalizar" panel: re-run the chart aggregator for an
  // adjusted spec (Periodo/Desglosar) without going through the LLM.
  // `source` is the It.3 declaration of the RECOMPUTED result, lifted next to
  // spec/data so the source line can be refreshed after a Periodo/Desglosar
  // change (AC-7). Optional: an account served by a backend without It.3 gets
  // spec/data and the line keeps whatever the message declared.
  recompute: (spec: ChartSpec) =>
    client.post<{ spec: ChartSpec; data: ChartData; source?: MessageSource | null }>(
      '/dashboards/recompute',
      { spec },
    ),

  // It.4 export. Two calls on purpose: `createExport` is what the user's click
  // does, and it is deliberately NOT part of the offer, so a download the model
  // merely mentioned costs nothing until it is asked for.
  //
  // The scope is never sent: the backend reads it off the message's own persisted
  // sources. Passing filters from here would let the file cover more than the
  // conversation reported (and more than the tools' permission scope allowed).
  createExport: (args: {
    conversationId: number;
    messageId: number;
    moduleKey: string;
    formatKey?: string;
    /** Only for a format whose export refuses to run without a range when the answer
     * declared none. The backend ignores it otherwise, so it cannot widen the file. */
    startDate?: string;
    endDate?: string;
    /** Which document, for a single-record format (the Firmas PDF) whose query
     * covered several. The backend validates it against the same candidate set it
     * offered, so this narrows the export and can never widen it. */
    recordId?: number;
  }) =>
    client.post<ExportArtifact>(
      `/chatbot/conversations/${args.conversationId}/messages/${args.messageId}/export`,
      {
        module_key: args.moduleKey,
        format_key: args.formatKey,
        start_date: args.startDate,
        end_date: args.endDate,
        record_id: args.recordId,
      },
    ),

  // Reads an artifact's state. Deferred exports (Respuestas, Lugares, Listas) stay
  // `pending` until their job finishes, so the card polls this.
  getExportArtifact: (id: number) =>
    client.get<ExportArtifact>(`/chatbot/export_artifacts/${id}`),

  // AskDataScope V2 click-to-detail: the records behind one clicked segment.
  segmentDetail: (spec: ChartSpec, dimensionValue: string, seriesValue?: string) =>
    client.post<{ count: number; records: SegmentRecord[] }>('/dashboards/segment_detail', {
      spec,
      dimension_value: dimensionValue,
      series_value: seriesValue,
    }),

  // Thumbs up/down on one answer. Passing the current vote again (or null)
  // clears it. The backend derives the question server-side, so we send only
  // the vote.
  // signal: lets the caller abort an in-flight vote (a newer vote superseding
  // it, or the component unmounting) instead of letting an abandoned request
  // resolve later and silently overwrite whatever vote is now current.
  submitFeedback: (
    conversationId: number,
    messageId: number,
    vote: 'up' | 'down' | null,
    signal?: AbortSignal,
  ) =>
    client.post<{ vote: 'up' | 'down' | null }>(
      `/chatbot/conversations/${conversationId}/messages/${messageId}/feedback`,
      { vote },
      { signal },
    ),
};

export interface SegmentRecord {
  id: number;
  label: string;
  date: string | null;
  /** Deep-link to the individual record, or null when the source has no route. */
  link: string | null;
}
